import * as React from 'react';
import Select, { Props as ReactSelectProps } from 'react-select';
import { styled, Theme } from '../../theme';
import { StylesConfig, components } from 'react-select';
import { useTheme } from '@emotion/react';

export type SelectOptionType = { label: string; value: string };

export interface SelectWidgetProps extends ReactSelectProps {
  hideDropdown?: boolean;
  getDecorations?: (value: any) => JSX.Element | null;
}

namespace S {
  export const Right = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `;
}

export const SelectWidget: React.FC<SelectWidgetProps> = (props) => {
  const { hideDropdown, ...rest } = props;
  const theme = useTheme() as Theme;
  return (
    <Select
      styles={EvaluateSelectStyles(theme, { isDisabled: props.isDisabled, hideDropdownIndicator: hideDropdown })}
      components={{
        ValueContainer: ({ children, ...rest }) => (
          <components.ValueContainer {...rest}>
            <S.Right>
              {children}
              {props.value ? props.getDecorations?.(props.value) : null}
            </S.Right>
          </components.ValueContainer>
        ),
        Option: ({ children, ...rest }) => (
          <components.Option {...rest}>
            <S.Right>
              {children}
              {props.getDecorations?.(rest)}
            </S.Right>
          </components.Option>
        )
      }}
      {...rest}
    />
  );
};

export function EvaluateSelectStyles(
  theme: Theme,
  rootState: { isDisabled?: boolean; hideDropdownIndicator?: boolean } = {}
): StylesConfig {
  return {
    control: (provided, state) => ({
      ...provided,
      background: theme.forms.input.background,
      color: theme.text.primary,
      opacity: 1,
      borderColor: state.isDisabled
        ? theme.select.borderDisabled
        : state.menuIsOpen
          ? theme.select.borderActive
          : theme.select.border,
      ':hover': {
        borderColor: theme.select.borderActive
      },
      height: 43,
      // This line disable the blue border
      boxShadow: 'none',
      cursor: rootState.isDisabled
        ? 'default'
        : state.menuIsOpen || rootState.hideDropdownIndicator
          ? 'text'
          : 'pointer'
    }),
    dropdownIndicator: (provided) => {
      return {
        ...provided,
        color: rootState.isDisabled ? theme.select.borderDisabled : theme.select.indicator,
        ':hover': { color: theme.select.indicator },
        display: rootState.hideDropdownIndicator ? 'none' : provided.display
      };
    },
    indicatorSeparator: (provided) => ({
      display: 'none'
    }),
    input: (provided) => {
      return {
        ...provided,
        opacity: 1,
        color: rootState.isDisabled ? theme.text.secondary : theme.select.inputColor
      };
    },
    singleValue: (provided) => {
      return {
        ...provided,
        color: rootState.isDisabled ? theme.text.secondary : theme.select.displayColor
      };
    },
    menu: (provided) => {
      return {
        ...provided,
        color: theme.text.primary,
        background: theme.select.background
      };
    },
    menuList: (provided) => {
      return {
        ...provided,
        '::-webkit-scrollbar': {
          width: 14,
          background: theme.header.borderColor
        },
        '::-webkit-scrollbar-track': {
          background: 'none'
        },
        '::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: 10,
          border: `solid 2px ${theme.header.borderColor}`
        }
      };
    },
    option: (styles: any, state) => {
      return {
        ...styles,
        ':active': {
          ...styles[':active'],
          backgroundColor: state.isSelected ? theme.select.optionSelected : 'transparent'
        },
        ':hover': {
          ...styles[':hover'],
          backgroundColor: theme.select.optionsHover
        },
        backgroundColor: state.isSelected ? theme.select.optionSelected : 'transparent',
        cursor: 'pointer'
      };
    }
  };
}
